import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  calculateAdminOwnerAnalyticsSummary,
  type AdminOwnerDailyAnalyticsData,
} from '../domain/adminOwnerAnalytics';

interface AdminOwnerAnalyticsDashboardProps {
  data: AdminOwnerDailyAnalyticsData;
}

const formatMetric = (value: number): string =>
  new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 }).format(value);

const AdminOwnerAnalyticsDashboard: React.FC<
  AdminOwnerAnalyticsDashboardProps
> = ({ data }) => {
  const summary = useMemo(
    () => calculateAdminOwnerAnalyticsSummary(data),
    [data],
  );
  const hasAvailableData = data.daily.some((day) => day.available);
  const displayMetric = (value: number): string =>
    hasAvailableData ? formatMetric(value) : '—';
  const maxActiveOwnerArchives = Math.max(
    1,
    ...data.daily
      .filter((day) => day.available)
      .map((day) => day.activeOwnerArchives),
  );

  return (
    <Dashboard>
      <Cards>
        <MetricCard>
          <MetricLabel>기간 활성 소유자 아카이브</MetricLabel>
          <MetricValue>
            {displayMetric(summary.periodActiveOwnerArchives)}
          </MetricValue>
        </MetricCard>
        <MetricCard>
          <MetricLabel>기간 자기 아카이브 방문</MetricLabel>
          <MetricValue>
            {displayMetric(summary.totalOwnerArchiveViews)}
          </MetricValue>
        </MetricCard>
        <MetricCard>
          <MetricLabel>일평균 활성 소유자 아카이브</MetricLabel>
          <MetricValue>
            {displayMetric(summary.averageDailyActiveOwnerArchives)}
          </MetricValue>
        </MetricCard>
        <MetricCard>
          <MetricLabel>최고 일 활성 소유자 아카이브</MetricLabel>
          <MetricValue>
            {displayMetric(summary.peakDailyActiveOwnerArchives)}
          </MetricValue>
        </MetricCard>
      </Cards>

      <ChartSection>
        <SectionTitle>일별 활성 소유자 아카이브</SectionTitle>
        <Chart aria-label="일별 활성 소유자 아카이브 막대 그래프">
          {data.daily.map((day) => {
            const value = day.available ? day.activeOwnerArchives : null;
            return (
              <BarColumn
                key={day.date}
                title={
                  value === null
                    ? `${day.date}: 집계 불가`
                    : `${day.date}: ${value}개`
                }
              >
                <BarValue>{value === null ? '—' : value}</BarValue>
                <BarTrack>
                  <Bar
                    $height={
                      value === null
                        ? 0
                        : (value / maxActiveOwnerArchives) * 100
                    }
                  />
                </BarTrack>
                <BarDate>{day.date.slice(5)}</BarDate>
              </BarColumn>
            );
          })}
        </Chart>
      </ChartSection>

      <Note>
        자기 아카이브 진입을 나타내며 생성·수정 행동과 외부 방문을 포함하지
        않습니다.{' '}
        {data.firstOwnerActivityDate
          ? `첫 소유자 활동 기록: ${data.firstOwnerActivityDate}`
          : '아직 소유자 활동 기록이 없습니다.'}
      </Note>

      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>날짜</th>
              <th>자기 아카이브 방문</th>
              <th>소유자 세션</th>
              <th>활성 소유자 아카이브</th>
            </tr>
          </thead>
          <tbody>
            {[...data.daily].reverse().map((day) => (
              <tr key={day.date}>
                <td>{day.date}</td>
                <td>
                  {day.available ? formatMetric(day.ownerArchiveViews) : '—'}
                </td>
                <td>
                  {day.available ? formatMetric(day.ownerSessions) : '—'}
                </td>
                <td>
                  {day.available
                    ? formatMetric(day.activeOwnerArchives)
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
    </Dashboard>
  );
};

export default AdminOwnerAnalyticsDashboard;

const Dashboard = styled.section`
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 20px;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
`;

const MetricCard = styled.div`
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  background: ${({ theme }) => theme.colors.color_Alpha_031};
`;

const MetricLabel = styled.div`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const MetricValue = styled.div`
  ${({ theme }) => theme.typography.Body_01_1};
`;

const ChartSection = styled.section`
  min-height: 230px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const Chart = styled.div`
  height: 190px;
  display: flex;
  align-items: stretch;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const BarColumn = styled.div`
  width: 28px;
  min-width: 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BarValue = styled.span`
  height: 18px;
  ${({ theme }) => theme.typography.Footer_02};
`;

const BarTrack = styled.div`
  width: 100%;
  min-height: 140px;
  display: flex;
  align-items: flex-end;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const Bar = styled.div<{ $height: number }>`
  width: 100%;
  min-height: ${({ $height }) => ($height > 0 ? '2px' : '0')};
  height: ${({ $height }) => `${$height}%`};
  background: ${({ theme }) => theme.colors.color_Gray_02};
`;

const BarDate = styled.span`
  margin-top: 4px;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Footer_02};
`;

const Note = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const TableWrapper = styled.div`
  min-height: 200px;
  overflow: auto;
  border-top: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  ${({ theme }) => theme.typography.Body_04};

  th,
  td {
    padding: 8px;
    text-align: right;
    border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_06};
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }
`;
